/**
 * Опросы. Каждый шаблон — свой скелет + css.
 *  • card       — карточка, вопрос по одному, прогресс, назад/далее (Яндекс Формы/Tilda).
 *  • fullscreen — один вопрос крупно по центру (Marquiz/Tilda «на весь экран»).
 *  • nps        — одна шкала 0–10 + комментарий (Delighted/Wootric).
 */
import type { RuntimeWidgetConfig, SurveyContent, SurveyQuestion } from '@inbx/shared/schemas';

import { clear, el } from '../dom';
import { isValidEmail, isValidPhone, maskPhone } from '../validation';

import type { BuildCtx, BuildInstance, WidgetTemplate } from './types';

function control(q: SurveyQuestion, answers: Record<string, unknown>): HTMLElement {
  const wrap = el('div', 'w-control');
  if (q.type === 'open_text') {
    const ta = el('textarea');
    ta.rows = 3;
    ta.value = (answers[q.id] as string) ?? '';
    ta.addEventListener('input', () => (answers[q.id] = ta.value));
    wrap.appendChild(ta);
    return wrap;
  }
  if (q.type === 'email' || q.type === 'phone') {
    const i = el('input');
    i.value = (answers[q.id] as string) ?? '';
    if (q.type === 'phone') {
      i.type = 'tel';
      i.inputMode = 'tel';
      i.placeholder = '+7 (___) ___-__-__';
      i.addEventListener('input', () => {
        i.value = maskPhone(i.value);
        answers[q.id] = i.value;
      });
    } else {
      i.type = 'email';
      i.inputMode = 'email';
      i.addEventListener('input', () => (answers[q.id] = i.value.trim()));
    }
    wrap.appendChild(i);
    return wrap;
  }
  if (q.type === 'single_choice' || q.type === 'multiple_choice') {
    const multi = q.type === 'multiple_choice';
    const box = el('div', 'w-options');
    for (const opt of q.options ?? []) {
      const o = el('div', 'w-option', opt);
      const sel = multi
        ? ((answers[q.id] as string[]) ?? []).includes(opt)
        : answers[q.id] === opt;
      if (sel) o.classList.add('selected');
      o.addEventListener('click', () => {
        if (multi) {
          const arr = (answers[q.id] as string[]) ?? [];
          const has = arr.includes(opt);
          answers[q.id] = has ? arr.filter((x) => x !== opt) : [...arr, opt];
          o.classList.toggle('selected', !has);
        } else {
          box.querySelectorAll('.w-option').forEach((e) => e.classList.remove('selected'));
          o.classList.add('selected');
          answers[q.id] = opt;
        }
      });
      box.appendChild(o);
    }
    wrap.appendChild(box);
    return wrap;
  }
  if (q.type === 'image_choice') {
    const box = el('div', 'w-image-options');
    for (const o of q.image_options ?? []) {
      const cell = el('div', 'w-image-option');
      const img = el('img');
      img.src = o.image_url;
      img.alt = o.label ?? '';
      cell.appendChild(img);
      if (o.label) cell.appendChild(el('div', 'w-image-label', o.label));
      if (answers[q.id] === o.value) cell.classList.add('selected');
      cell.addEventListener('click', () => {
        box.querySelectorAll('.w-image-option').forEach((e) => e.classList.remove('selected'));
        cell.classList.add('selected');
        answers[q.id] = o.value;
      });
      box.appendChild(cell);
    }
    wrap.appendChild(box);
    return wrap;
  }
  if (q.type === 'rating') {
    const rt = q.rating_type ?? 'stars';
    const row = el('div', 'w-rating');
    const range = rt === 'nps' ? [...Array(11).keys()] : [1, 2, 3, 4, 5];
    const cur = answers[q.id];
    for (const i of range) {
      const on = rt === 'stars' ? typeof cur === 'number' && i <= cur : cur === i;
      const b = el('button', on ? 'w-rate-btn is-on' : 'w-rate-btn', rt === 'nps' ? String(i) : rt === 'emoji' ? ['😞', '😕', '😐', '🙂', '😄'][i - 1] : '★');
      b.type = 'button';
      b.addEventListener('click', () => {
        answers[q.id] = i;
        row.querySelectorAll('.w-rate-btn').forEach((e, idx) =>
          e.classList.toggle('is-on', rt === 'stars' ? idx < i : e === b),
        );
      });
      row.appendChild(b);
    }
    wrap.appendChild(row);
    if (q.rating_min_label || q.rating_max_label) {
      const l = el('div', 'w-rate-labels');
      l.append(el('span', undefined, q.rating_min_label ?? ''), el('span', undefined, q.rating_max_label ?? ''));
      wrap.appendChild(l);
    }
    return wrap;
  }
  // scale
  const min = q.scale_min ?? 1;
  const max = q.scale_max ?? 10;
  const stepv = q.scale_step ?? 1;
  const row = el('div', 'w-rating');
  for (let i = min; i <= max; i += stepv) {
    const b = el('button', answers[q.id] === i ? 'w-rate-btn is-on' : 'w-rate-btn', String(i));
    b.type = 'button';
    b.addEventListener('click', () => {
      answers[q.id] = i;
      row.querySelectorAll('.w-rate-btn').forEach((e) => e.classList.toggle('is-on', e === b));
    });
    row.appendChild(b);
  }
  wrap.appendChild(row);
  return wrap;
}

function buildSurvey(ctx: BuildCtx, variant: 'card' | 'fullscreen' | 'nps'): BuildInstance {
  const c = (ctx.config.content ?? {}) as SurveyContent;
  const qs = (c.questions ?? []) as SurveyQuestion[];
  const answers: Record<string, unknown> = {};

  const result = (ok: boolean): void => {
    clear(ctx.root);
    const box = el('div', 'w-survey');
    const r = el('div', ok ? 'w-success' : 'w-error');
    r.append(
      el('div', 'w-result-icon', ok ? '✓' : '✕'),
      el('div', 'w-result-text', ok ? (c.completion_message ?? 'Спасибо за ответы!') : 'Не удалось отправить. Попробуйте ещё раз.'),
    );
    box.appendChild(r);
    ctx.root.appendChild(box);
  };
  const finish = async (): Promise<void> => {
    try {
      await ctx.handlers.onSubmit?.({ fields: { ...answers }, survey: true });
      result(true);
    } catch {
      result(false);
    }
  };

  if (ctx.initialSuccess) { void finish(); return {}; }

  if (variant === 'nps') {
    const box = el('div', 'w-survey');
    const nps = qs.find((q) => q.type === 'rating' && q.rating_type === 'nps') ?? qs[0];
    const txt = qs.find((q) => q.type === 'open_text');
    if (nps) { box.appendChild(el('h3', undefined, nps.text)); box.appendChild(control(nps, answers)); }
    if (txt) { box.appendChild(el('h3', undefined, txt.text)); box.appendChild(control(txt, answers)); }
    const submit = el('button', 'w-submit', 'Отправить');
    submit.type = 'button';
    submit.addEventListener('click', () => void finish());
    box.appendChild(submit);
    ctx.root.appendChild(box);
    return {};
  }

  // Навигация с ВЕТВЛЕНИЯМИ: история пройденных индексов + переход по ответу.
  const idxById = (id: string): number => qs.findIndex((q) => q.id === id);
  const history: number[] = [0];
  /** Следующий индекс с учётом branches; -1 = завершить. */
  const nextIndex = (q: SurveyQuestion, here: number): number => {
    const ans = answers[q.id];
    for (const b of q.branches ?? []) {
      const hit = Array.isArray(ans) ? ans.includes(b.when.answer) : ans === b.when.answer;
      if (hit) return b.goto === '__end__' ? -1 : idxById(b.goto);
    }
    return here + 1;
  };

  const render = (): void => {
    clear(ctx.root);
    const idx = history[history.length - 1]!;
    const q = qs[idx];
    const box = el('div', 'w-survey');
    if (!q) { box.appendChild(el('div', 'w-success', 'Нет вопросов')); ctx.root.appendChild(box); return; }
    if (c.show_progress && qs.length > 1) {
      const p = el('div', 'w-progress');
      const fill = el('div', 'w-progress-fill');
      fill.style.width = `${Math.min(100, Math.round((history.length / qs.length) * 100))}%`;
      p.appendChild(fill);
      box.appendChild(p);
    }
    box.appendChild(el('h3', undefined, q.text + (q.required ? ' *' : '')));
    box.appendChild(control(q, answers));
    const nav = el('div', 'w-nav');
    if (c.allow_back && history.length > 1) {
      const back = el('button', 'w-back', 'Назад');
      back.type = 'button';
      back.addEventListener('click', () => { history.pop(); render(); });
      nav.appendChild(back);
    }
    const next = el('button', 'w-next', 'Далее');
    next.type = 'button';
    const showMsg = (text: string): void => {
      box.querySelector('.w-field-msg')?.remove();
      const m = el('div', 'w-field-msg', text);
      nav.before(m);
    };
    next.addEventListener('click', () => {
      const a = answers[q.id];
      const empty = a === undefined || a === '' || (Array.isArray(a) && a.length === 0);
      if (q.required && empty) { showMsg('Ответьте на вопрос'); return; }
      if (!empty && q.type === 'email' && !isValidEmail(String(a))) { showMsg('Введите корректный e-mail'); return; }
      if (!empty && q.type === 'phone' && !isValidPhone(String(a))) { showMsg('Введите телефон полностью'); return; }
      const ni = nextIndex(q, idx);
      if (ni < 0 || ni >= qs.length) { void finish(); return; }
      history.push(ni);
      render();
    });
    // Подпись кнопки: «Готово», если дальше некуда.
    if (nextIndex(q, idx) < 0 || nextIndex(q, idx) >= qs.length) next.textContent = 'Готово';
    nav.appendChild(next);
    box.appendChild(nav);
    ctx.root.appendChild(box);
  };
  render();
  return {};
}

export const SURVEY_TEMPLATES: WidgetTemplate[] = [
  { id: 'survey-card', type: 'survey', build: (c) => buildSurvey(c, 'card') },
  { id: 'survey-fullscreen', type: 'survey', build: (c) => buildSurvey(c, 'fullscreen') },
  { id: 'survey-nps', type: 'survey', build: (c) => buildSurvey(c, 'nps') },
];
